import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import type { Size } from '@genially/design-system';
import {
  BasicParagraph,
  BiggerSubtitle,
  BigParagraph,
  BigSubtitle,
  SmallParagraph,
  style,
} from '@genially/design-system';
import ReplaceIcon from '@genially/icons/lib/replace/ReplaceIcon';

interface WrapperProps {
  $isOpen?: boolean;
  $isFullWidth?: boolean;
  $viewportWidth?: number;
  $hasPaddings?: boolean;
  $hasReducedPadding?: boolean;
  $removeSidePaddings?: boolean;
}

export const StyledBigSubtitle = styled(BigSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const StyledBiggerSubtitle = styled(BiggerSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const QuestionTitleWrapper = styled.div(() => ({
  width: '100%',
}));

const BaseQuestionTitle = (Component: ElementType) =>
  styled(Component)(({ theme: { color } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  }));

export const QuestionTitleBigParagraph = styled(BaseQuestionTitle(BigParagraph))({
  '-webkit-line-clamp': '2',
});

export const QuestionTitleBasicParagraph = styled(BaseQuestionTitle(BasicParagraph))({
  '-webkit-line-clamp': '4',
});

export const TotalParticipantsBigSubtitle = styled(BigSubtitle)(
  ({ theme: { color } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
  }),
);

export const DataWrapper = styled.div(({ theme: { size }, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: size.units(1.5),
  overflowY: 'auto',
  width: '100%',
  textAlign: 'center',
  padding: `0 ${size.units(2)} 0 ${size.units(1.5)}`,

  ...style.mixins.scrollbar(theme),
}));

export const LastUpdateWrapper = styled.div(({ theme: { size } }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: size.units(1.5),
  paddingLeft: size.units(1.5),
}));

export const TextWrapper = styled.div(() => ({
  textAlign: 'center' as const,
}));

export const StyledSmallParagraph = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const StyledSmallParagraphDate = styled(SmallParagraph)(
  ({ theme: { color } }) => ({
    color: color.content.reversed.default(),
  }),
);

export const StyledReplaceIcon = styled(ReplaceIcon)(({ theme: { color, size } }) => ({
  width: size.units(2),
  height: size.units(2),
  minWidth: size.units(2),
  color: color.content.reversed.default(),
  cursor: 'pointer',
}));

const fadeIn = keyframes({
  '0%': {
    opacity: 0,
  },
  '100%': {
    opacity: 1,
  },
});

const withAnimation = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        & > * {
          animation: ${fadeIn} 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        }
      `,
  );

export const FooterWrapper = styled.div({
  width: '100%',
});

export const ContentWrapper = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column' as const,
  width: '100%',
  overflow: 'hidden',
  textAlign: 'center' as const,
  height: '100%',
}));

interface SizeButtonWrapperProps {
  $hiddenMobile?: boolean;
  $hiddenFullWidth?: boolean;
  $hidden?: boolean;
}

export const SizeButtonWrapper = styled.div<SizeButtonWrapperProps>(
  ({ theme: { transition }, $hiddenMobile, $hiddenFullWidth, $hidden }) => ({
    display: $hidden ? 'none' : undefined,
    opacity: $hidden ? 0 : 1,
    transition: transition.standard('opacity'),
    pointerEvents: $hidden ? 'none' : undefined,

    ...style.mixins.responsive.sm.upward({
      display: $hiddenFullWidth ? 'none' : undefined,
    }),

    ...style.mixins.responsive.sm.downward({
      display: $hiddenMobile ? 'none' : undefined,
    }),
  }),
);

const getSidebarPaddings = (
  size: Size,
  isOpen?: boolean,
  hasPaddings?: boolean,
  removeSidePaddings?: boolean,
) => {
  if (removeSidePaddings) {
    return `0 0 ${size.units(2)}`;
  }

  if (isOpen && hasPaddings) {
    return `0 ${size.units(4)} ${size.units(2)}`;
  }

  return 0;
};

export const SidebarWrapper = withAnimation(
  styled.div<WrapperProps>(
    ({
      theme: { size, color, transition },
      $isOpen,
      $isFullWidth,
      $viewportWidth,
      $hasPaddings,
      $hasReducedPadding,
      $removeSidePaddings,
    }) => {
      return {
        height: '100%',
        width: $isFullWidth && $viewportWidth ? `${$viewportWidth}px` : '100%',
        backgroundColor: color.background.reversed.default(),
        overflow: 'hidden',
        transition: transition.standard('width', 'background-color', 'padding'),
        color: color.content.reversed.default(),
        padding: getSidebarPaddings(size, $isOpen, $hasPaddings, $removeSidePaddings),
        position: 'absolute',
        top: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',

        ...style.mixins.responsive.sm.downward({
          position: 'absolute',
          right: 0,
          backgroundColor: color.background.reversed.default(),
          width: $isOpen ? '100%' : 0,
        }),

        ...($isFullWidth && {
          padding: $hasReducedPadding
            ? `${size.units(2)} ${size.units(5)}`
            : `${size.units(9)} ${size.units(20)} ${size.units(6.5)}`,
        }),

        [SizeButtonWrapper]: {
          position: 'absolute',
          top: size.units(2),
          zIndex: 1,

          ':nth-child(1)': {
            left: size.units(2),
          },
          ':nth-child(2)': {
            right: size.units(2),
          },
        },

        [QuestionTitleBigParagraph]: {
          marginTop: size.units(1.5),
          marginBottom: size.units(3),
        },

        [LastUpdateWrapper]: {
          marginTop: size.units(2),
        },

        ...style.mixins.responsive.sm.downward({
          ...style.mixins.increaseSpecificity({
            [`${SizeButtonWrapper}:nth-child(1)`]: {
              left: 'auto',
              right: size.units(2),
            },
          }),
        }),
      };
    },
  ),
);

interface WrapperProps {
  $isOpen?: boolean;
}

export const Wrapper = styled.div<WrapperProps>(
  ({ theme: { size, transition }, $isOpen, $viewportWidth }) => ({
    position: 'relative',
    width: $isOpen ? size.units(50) : 0,
    maxWidth: '100%',
    height: '100%',
    transition: transition.standard('width'),

    ...style.mixins.responsive.sm.downward({
      width: $isOpen ? `${$viewportWidth}px` : 0,
    }),
  }),
);

interface ChildrenMansoryWrapperProps {
  $hasScrollBottom: boolean;
  $hasScrollTop: boolean;
}

export const ChildrenMansoryWrapper = styled.div<ChildrenMansoryWrapperProps>(
  ({ theme: { size, color, transition }, $hasScrollBottom, $hasScrollTop }) => ({
    position: 'relative',
    width: '100%',
    minHeight: 0,

    '::after, ::before': {
      position: 'absolute',
      content: '""',
      left: 0,
      width: '100%',
      height: size.units(9),
      pointerEvents: 'none',
      transition: transition.standard('opacity'),
      background: `linear-gradient(0deg, rgba(255, 255, 255, 0), ${color.background.reversed.default()})`,
    },

    '::before': {
      top: -1,
      opacity: $hasScrollTop ? 1 : 0,
    },

    '::after': {
      bottom: -1,
      transform: 'rotate(180deg)',
      opacity: $hasScrollBottom ? 1 : 0,
    },
  }),
);

export const MasonryWrapper = styled.div(({ theme: { color, size }, theme }) => ({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  padding: `0 ${size.units(1.5)} ${size.units(4)}`,

  ...style.mixins.scrollbar(theme),

  scrollbarColor: `${color.background.primary.disabledContrast()} ${color.background.reversed.hover()}`,
}));
